import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { SERVICES } from "../data/services";
import SEO from "../components/SEO";

export default function Services({ onStartTax, onBookAppointment }) {
  return (
    <div data-testid="services-page" className="pt-16">
      <SEO path="/our-services"
        title="Our Services | Marizema Tax Service — Tampa, FL"
        description="Tax preparation, small business accounting, tax planning, financial consulting and bookkeeping services for Tampa, Florida." />
      <section className="py-16 sm:py-24 bg-[#0B2545]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-widest font-semibold text-[#D4AF37] mb-3">Our Services</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl font-bold tracking-tight text-white max-w-3xl">
            Comprehensive tax & financial services
          </h1>
          <p className="mt-4 text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed">
            Tailored expertise for Tampa residents and small business owners — from annual returns to year-round strategy.
          </p>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          {SERVICES.map((s, i) => (
            <motion.article
              key={s.slug}
              data-testid={`services-detail-${s.slug}`}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5 }}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-center bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}
            >
              <div className="h-56 lg:h-full min-h-[220px]">
                <img src={s.image} alt={s.name} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-7 sm:p-10">
                <h2 className="font-serif-display text-2xl sm:text-3xl font-semibold text-[#0B2545]">{s.name}</h2>
                <p className="mt-3 text-[#1F2933]/80 leading-relaxed">{s.summary}</p>
                <ul className="mt-5 space-y-2.5">
                  {s.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-[#1F2933]/85">
                      <CheckCircle2 size={16} className="text-[#86680A] mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-7 flex flex-wrap gap-3">
                  <button data-testid={`services-cta-start-${s.slug}`} onClick={onStartTax}
                    className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#C9A227] text-[#0B2545] text-sm font-bold hover:bg-[#D4AF37] transition-colors">
                    Start Tax Return <ArrowRight size={15} />
                  </button>
                  {s.dedicated && (
                    <Link data-testid="services-cta-bookkeeping" to="/marizema-bookkeeping-service"
                      className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full border border-[#0B2545]/40 text-[#0B2545] text-sm font-bold hover:bg-[#0B2545]/5 transition-colors">
                      Bookkeeping Details
                    </Link>
                  )}
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="py-16 bg-[#14375E] text-center">
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="font-serif-display text-2xl sm:text-3xl font-semibold text-white">Not sure which service fits?</h2>
          <p className="mt-3 text-slate-300">Book a free consultation and we'll point you in the right direction — no obligation.</p>
          <button data-testid="services-cta-book-appointment" onClick={onBookAppointment}
            className="mt-7 px-7 py-3.5 rounded-full bg-[#C9A227] text-[#0B2545] text-sm font-bold hover:bg-[#D4AF37] transition-colors">
            Book Appointment
          </button>
        </div>
      </section>
    </div>
  );
}
